import { useNavigate } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import {
  Mail, FileSearch, UserCheck, Eye, CheckCircle2, ArrowRight,
  HardHat, ClipboardCheck, ShieldCheck, Lock, Package, FileText,
  Link2, Upload, QrCode, Send, Bell, Users, Globe, Truck,
  Inbox, Search, AlertTriangle, Clock, Sparkles, Camera, Image,
  FileUp, Calendar, Phone, Shield, MapPin, Map, Settings2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

function Section({ id, children, className = "" }) {
  const [ref, visible] = useScrollReveal(0.06);
  return (
    <section id={id} ref={ref} className={`section-reveal ${visible ? "visible" : ""} ${className}`}>
      {children}
    </section>
  );
}

// S2: Why builders use easypermit
export function WhyBuilders() {
  const cards = [
    { icon: Mail, title: "Important permit emails get buried", desc: "Council requests, decisions, and proof sit in shared inboxes. Nobody knows who has seen them or what happened next." },
    { icon: Users, title: "Nobody knows who is holding the job up", desc: "A soil report is overdue. A consultant has not responded. An office review is pending. Nobody is tracking it." },
    { icon: AlertTriangle, title: "Trades or inspections get booked before the stage is actually ready", desc: "The slab is scheduled but 2 conditions are still open. The concreter arrives and the stage is not cleared." },
  ];

  return (
    <Section id="why" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">
            Because the next stage should not be a guess.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card, i) => (
            <Card key={i} className="border-slate-200 bg-white hover:shadow-lg transition-all group hover-lift">
              <CardContent className="pt-8 pb-7 px-7">
                <div className="w-12 h-12 bg-red-50 border border-red-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-red-100 transition-colors">
                  <card.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-heading font-semibold text-base text-slate-900 mb-3">{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Section>
  );
}

// S3: How easypermit works (6-step visual journey)
export function HowItWorks() {
  const steps = [
    { icon: Mail, title: "Emails and PDFs come in automatically", desc: "Council correspondence is captured from Outlook 365 or Gmail. AI classifies it into requests, decisions, proof, or general." },
    { icon: FileSearch, title: "Council requests and conditions are extracted", desc: "RFI items and permit conditions are turned into structured objects with owners, due dates, and proof requirements." },
    { icon: UserCheck, title: "Owners, proof, and certificate requirements are assigned", desc: "Each item gets a clear owner. The team knows who needs to respond, what proof is required, and when." },
    { icon: Eye, title: "Proof is reviewed", desc: "Files arrive from consultants, site QR uploads, and secure links. AI suggests condition matches. A human reviews before anything clears." },
    { icon: ShieldCheck, title: "Stage and inspection readiness update", desc: "As conditions clear and proof is approved, stage readiness recalculates. Trade release and surveyor notification states update." },
    { icon: Truck, title: "Trades, surveyor notifications, and client updates happen with confidence", desc: "The team knows when a trade is safe to release, when to notify the surveyor, and when to share a client update." },
  ];

  return (
    <Section id="how-it-works" className="py-24 bg-slate-50/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">How easypermit works</h2>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">From council inbox to confident trade release in six structured steps. Every step has a human in the loop.</p>
        </div>
        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent -translate-y-1/2 z-0" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
            {steps.map((step, i) => (
              <div key={i} className="relative group">
                <Card className="border-slate-200 bg-white hover:shadow-xl hover:border-blue-200 transition-all h-full">
                  <CardContent className="pt-7 pb-6 px-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                        <step.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider">Step {i + 1}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-sm text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{step.desc}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

// S4: How we are different
export function HowDifferent() {
  return (
    <Section id="different" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">How we are different</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <Card className="border-slate-300 bg-slate-50">
            <CardContent className="pt-7 pb-6 px-7">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-4">Most tools manage the build</p>
              <ul className="space-y-3">
                {["Schedules", "Chats", "Client updates", "General tasks"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300" /> {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/30 shadow-lg shadow-blue-100/30">
            <CardContent className="pt-7 pb-6 px-7">
              <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider mb-4">easypermit manages what can quietly stop the build</p>
              <ul className="space-y-3">
                {["Council requests", "Approval conditions", "Proof and certificates", "Stage readiness", "Trade and inspection readiness"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-800 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
}

// S5: Real planning permit example
export function RealExample() {
  const conditions = [
    { stage: "Pre-Start", items: ["Construction Management Plan", "Sediment and erosion controls", "TPZ fencing"], cleared: 3, total: 3, ready: true },
    { stage: "Pre-Slab", items: ["Drainage certificate", "Soil report verification", "Stormwater connection", "Setback confirmation"], cleared: 1, total: 4, ready: false },
    { stage: "Pre-Frame", items: ["Shadow diagrams update", "Neighbourhood character response"], cleared: 0, total: 2, ready: false },
    { stage: "Pre-OC", items: ["Traffic response", "Final landscaping plan"], cleared: 0, total: 2, ready: false },
  ];

  return (
    <Section className="py-24 bg-slate-50/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">Real planning permit example</h2>
          <p className="text-base text-slate-500">12 Smith St, Werribee - Wyndham Council - WCC-2024-0891</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
          <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-white">Planning Permit Decision Imported</p>
              <p className="text-xs text-slate-400">11 conditions found across 4 stages</p>
            </div>
            <div className="flex gap-2">
              <span className="bg-emerald-500/20 text-emerald-300 text-[10px] font-medium px-2.5 py-0.5 rounded-full">3 cleared</span>
              <span className="bg-red-500/20 text-red-300 text-[10px] font-medium px-2.5 py-0.5 rounded-full">3 blockers</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-slate-200">
            {conditions.map((stage, i) => (
              <div key={i} className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{stage.stage}</h4>
                  {stage.ready ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600"><CheckCircle2 className="w-3 h-3" /> Ready</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600"><Lock className="w-3 h-3" /> Not ready</span>
                  )}
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
                  <div className={`h-1.5 rounded-full ${stage.ready ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${(stage.cleared / stage.total) * 100}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 mb-2">{stage.cleared}/{stage.total} cleared</p>
                <ul className="space-y-1.5">
                  {stage.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-slate-600">
                      {j < stage.cleared ? <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" /> : <Clock className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" />}
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

// S6: Builder workflow preview
export function BuilderPreview() {
  const navigate = useNavigate();
  const features = [
    { title: "New Approvals Mail", desc: "Council emails auto-captured from Outlook or Gmail", icon: Mail, path: "/builder/new-mail" },
    { title: "Council Requests", desc: "Request items with owners, due dates, and draft responses", icon: FileText, path: "/builder/rfi-war-room" },
    { title: "Stage Conditions", desc: "Conditions grouped by stage with proof requirements", icon: ShieldCheck, path: "/builder/stage-conditions" },
    { title: "Proof Review", desc: "Review proof, AI visual assist, approve to clear conditions", icon: Eye, path: "/builder/proof-review" },
    { title: "Stage Readiness", desc: "Is the stage actually ready? The centre of the story.", icon: Lock, path: "/builder/stage-readiness" },
    { title: "Inspection Readiness", desc: "Certificate tracking and surveyor notification readiness", icon: Search, path: "/builder/inspection-readiness" },
    { title: "Trade Release", desc: "Readiness-driven release board with trade contacts", icon: Truck, path: "/builder/trade-release" },
    { title: "Site Ready Pack", desc: "QR upload packs for site supervisors", icon: Package, path: "/builder/site-ready-pack" },
  ];

  return (
    <Section id="workflow" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-4 py-1.5 mb-6">
            <HardHat className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Builder workflow</span>
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">
            Approvals and readiness shield for builders
          </h2>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">
            Know what came in. Know what it means. Know who owns it. Know what is missing. Know whether the next stage is actually ready.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <Card key={i} className="border-slate-200 hover:border-blue-200 cursor-pointer hover:shadow-lg transition-all group hover-lift" onClick={() => navigate(f.path)} data-testid={`builder-feature-${i}`}>
              <CardContent className="pt-6 pb-5 px-5">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-50 transition-colors">
                  <f.icon className="w-5 h-5 text-slate-500 group-hover:text-blue-600 transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-sm text-slate-900 mb-1.5 group-hover:text-blue-600 transition-colors">{f.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Section>
  );
}

// S7: Surveyor workflow preview
export function SurveyorPreview() {
  const navigate = useNavigate();
  return (
    <Section className="py-24 bg-slate-50/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1 mb-5">
              <ClipboardCheck className="w-3.5 h-3.5 text-slate-600" />
              <span className="text-xs font-medium text-slate-600">Surveyor workflow</span>
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">Cleaner review and collaboration layer</h2>
            <p className="text-base text-slate-500 leading-relaxed mb-6">Receive better structured packs. Review proof by condition and stage. Request more without email back-and-forth. See source excerpts and audit trail.</p>
            <p className="text-sm text-slate-400 italic mb-8">This is a review layer only. Not a certifier replacement. Certification decisions remain outside this system.</p>
            <Button variant="outline" className="border-slate-300 gap-2" onClick={() => navigate("/surveyor")} data-testid="surveyor-cta">
              <ClipboardCheck className="w-4 h-4" /> Explore Surveyor View <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-900 px-4 py-2.5 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
              </div>
              <span className="text-[10px] text-slate-400 ml-2 font-mono">easypermit / surveyor / review-centre</span>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Proof</p>
                <div className="bg-white rounded-md p-2 border border-slate-200">
                  <div className="w-full h-14 bg-slate-100 rounded flex items-center justify-center"><FileText className="w-4 h-4 text-slate-300" /></div>
                  <p className="text-[10px] text-slate-600 mt-1 font-medium">Drainage_Cert.pdf</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Condition</p>
                <div className="bg-white rounded-md p-2 border border-slate-200 space-y-1">
                  <p className="text-[10px] font-medium text-slate-800">2.1 Drainage Certificate</p>
                  <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[9px] font-medium">Needs review</span>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Actions</p>
                <div className="space-y-1.5">
                  <div className="bg-emerald-50 text-emerald-700 text-[10px] font-medium px-2 py-1.5 rounded-md text-center">Accept</div>
                  <div className="bg-amber-50 text-amber-700 text-[10px] font-medium px-2 py-1.5 rounded-md text-center">Request more</div>
                  <div className="bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-1.5 rounded-md text-center">Add note</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// S8: How outsourced people fit in
export function OutsourcedFlow() {
  const steps = [
    { icon: Send, label: "Engineer receives AI-drafted request email with due date and context" },
    { icon: Link2, label: "Secure upload link included. No account required." },
    { icon: QrCode, label: "Site supervisor scans QR code to upload photos directly" },
    { icon: Upload, label: "Consultant uploads without needing a full account" },
    { icon: Eye, label: "Upload arrives in Proof Review. Reviewer checks and approves." },
    { icon: ShieldCheck, label: "Readiness updates. Trade release recalculates." },
  ];

  return (
    <Section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">How outsourced people fit in</h2>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">Consultants, engineers, and site teams can participate without a full account.</p>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all group hover-lift">
              <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                <step.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex items-center gap-3 flex-1">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider shrink-0">Step {i + 1}</span>
                <p className="text-sm text-slate-700">{step.label}</p>
              </div>
              {i < steps.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// S9: What gets generated
export function GeneratedPacks() {
  const packs = [
    { title: "Site Ready Pack", desc: "QR upload, secure links, condition checklist for site teams", icon: Package },
    { title: "Surveyor Ready Pack", desc: "Proof linked to conditions with source excerpts and audit trail", icon: ClipboardCheck },
    { title: "Weekly Risk Report", desc: "Stages not ready, blockers, overdue items, people holding jobs up", icon: AlertTriangle },
    { title: "Client Update Snapshot", desc: "Curated milestone update with AI-selected photos and softened language", icon: Globe },
    { title: "Before / After Collage", desc: "AI picks approved client-safe photos and generates a visual update", icon: Camera },
  ];

  return (
    <Section id="screens" className="py-24 bg-slate-50/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">What gets generated</h2>
          <p className="text-base text-slate-500 max-w-xl mx-auto">Packs, reports, and updates created from your approvals and readiness data.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {packs.map((p, i) => (
            <Card key={i} className="border-slate-200 bg-white hover:shadow-lg transition-all group hover-lift">
              <CardContent className="pt-7 pb-6 px-5 text-center">
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                  <p.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-semibold text-sm text-slate-900 mb-2">{p.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{p.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Section>
  );
}

// S10: Built for how builders work
export function BuiltForBuilders() {
  const points = [
    { icon: Mail, text: "Works with Outlook and Gmail. No new inbox to learn." },
    { icon: Users, text: "Works if consultants handle permits. Start from handover." },
    { icon: QrCode, text: "Works with QR uploads and secure links from site." },
    { icon: Shield, text: "Does not require everyone to learn a new system." },
    { icon: Calendar, text: "Connects to your existing calendar for reminders." },
  ];

  return (
    <Section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-6">Built for the way builders actually work</h2>
            <div className="space-y-4">
              {points.map((p, i) => (
                <div key={i} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                    <p.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed pt-2">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
          <Card className="border-slate-200 bg-slate-50 shadow-lg">
            <CardContent className="pt-7 pb-6 px-7">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Questions easypermit answers every day</p>
              <ul className="space-y-3">
                {[
                  "What do I need to do now?",
                  "Who do I send this to?",
                  "What is blocking the next stage?",
                  "Can I notify the surveyor yet?",
                  "Can I release the next trade yet?",
                  "What happens after this upload?",
                  "Is the next stage actually ready?",
                ].map((q, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700 font-medium">
                    <ArrowRight className="w-4 h-4 text-blue-600 shrink-0" /> {q}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
}

// S11: External People / Consultants section
export function ExternalPeopleSection() {
  const journey = [
    { step: 1, title: 'Builder sends request', desc: 'Request sent from easypermit with context, due date, and upload path.' },
    { step: 2, title: 'External person receives it', desc: 'Arrives via email, secure link, QR code, or WhatsApp. No login needed.' },
    { step: 3, title: 'They upload or reply', desc: 'Upload a cert, send site photos, or reply with an update. No account required.' },
    { step: 4, title: 'AI matches the response', desc: 'easypermit matches the upload to the correct job, condition, or blocker.' },
    { step: 5, title: 'Human reviews and approves', desc: 'Builder PM or admin reviews the submission. Flags or approves.' },
    { step: 6, title: 'Readiness updates', desc: 'Condition clears. Trade release updates. Packs and calendar reflect it.' },
  ];

  return (
    <Section className="py-24 bg-white" id="external-people">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">
            Work with consultants and site crews without another login
          </h2>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">
            easypermit sends the request, due date, context, and upload path to the right external person. They reply or upload through email, secure links, QR, or WhatsApp, and the result comes back into the job automatically.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Journey */}
          <div className="space-y-3">
            {journey.map((j, i) => (
              <div key={i} className="flex items-start gap-4 group">
                <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 group-hover:bg-blue-500 transition-colors">{j.step}</div>
                <div className="flex-1 pt-1">
                  <p className="text-sm font-semibold text-slate-900">{j.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{j.desc}</p>
                </div>
                {i < journey.length - 1 && <div className="w-px h-6 bg-slate-200 ml-[18px] mt-10 absolute" />}
              </div>
            ))}
          </div>

          {/* Right: Value panel + comparison */}
          <div className="space-y-5">
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-5 pb-4 px-5">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-3">Why builders like this</p>
                <div className="space-y-2">
                  {[
                    "No full account required for external people",
                    "Fewer chase calls and less friction",
                    "Uploads tied to the right condition or blocker",
                    "Site photos go straight into review",
                    "Audit trail stays clean",
                    "Readiness and trade release update from approved items",
                  ].map((v, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-blue-700"><CheckCircle2 className="w-3 h-3 text-blue-600 shrink-0" />{v}</div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <Card className="border-red-200 bg-red-50/30">
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-2">Normally</p>
                  <div className="space-y-1.5 text-[11px] text-red-700">
                    <p>Random calls, texts, attachments</p>
                    <p>Wrong file comes back</p>
                    <p>Admin manually sorts it</p>
                    <p>Trade still waits</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">With easypermit</p>
                  <div className="space-y-1.5 text-[11px] text-emerald-700">
                    <p>Request tied to condition</p>
                    <p>Upload returns to correct job</p>
                    <p>Review happens in one place</p>
                    <p>Readiness updates</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// S12: Project Start Story
export function ProjectStartSection() {
  const navigate = useNavigate();
  const steps = [
    { icon: MapPin, label: 'Address entered' },
    { icon: Map, label: 'Parcel found' },
    { icon: Globe, label: 'Council identified' },
    { icon: HardHat, label: 'Type inferred' },
    { icon: Settings2, label: 'Model selected' },
    { icon: CheckCircle2, label: 'Project created' },
  ];

  return (
    <Section className="py-24 bg-slate-50/80" id="project-start">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">
              Start a job with an address, not a spreadsheet
            </h2>
            <p className="text-base text-slate-500 mb-8">
              Enter the site address or upload what you already have. easypermit structures the project, identifies the council and parcel context, sets the workflow, and gets the team into the right readiness flow faster.
            </p>

            {/* Visual journey */}
            <div className="flex items-center gap-0 mb-8">
              {steps.map((s, i) => (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex-1 text-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-1.5">
                      <s.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-[9px] font-semibold text-slate-600">{s.label}</p>
                  </div>
                  {i < steps.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                </div>
              ))}
            </div>

            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" onClick={() => navigate('/builder/project-intake')} data-testid="landing-create-project-btn">
              <FileUp className="w-4 h-4" /> Try Create Project
            </Button>
          </div>

          <Card className="border-slate-200 bg-white shadow-lg">
            <CardContent className="pt-6 pb-5 px-6">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">What easypermit structures from the start</p>
              <div className="space-y-3">
                {[
                  { icon: MapPin, text: "Site address and project record" },
                  { icon: Globe, text: "Council and planning context" },
                  { icon: HardHat, text: "Operating model" },
                  { icon: Users, text: "Project contacts" },
                  { icon: Lock, text: "Scope boundaries" },
                  { icon: ShieldCheck, text: "Stage and readiness starting point" },
                  { icon: Upload, text: "Upload / review workflow" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-slate-700">
                    <div className="w-8 h-8 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center shrink-0">
                      <item.icon className="w-4 h-4 text-slate-500" />
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
}

// S13: Role selector
export function RoleSelector() {
  const navigate = useNavigate();
  return (
    <Section id="role-selector" className="py-24 bg-slate-50/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight mb-4">Explore the product</h2>
          <p className="text-base text-slate-500">Choose your view to see how easypermit works for your role.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            { title: "Builder View", desc: "Manage requests, conditions, proof, and readiness. See what blocks the next stage.", icon: HardHat, path: "/builder", primary: true },
            { title: "Surveyor View", desc: "Review packs, evidence, and milestone readiness. Collaboration layer.", icon: ClipboardCheck, path: "/surveyor", primary: false },
            { title: "Client Portal", desc: "Curated updates, milestone progress, and before/after photos.", icon: Users, path: "/shared/client-portal", primary: false },
          ].map((role, i) => (
            <Card key={i} className="border-slate-200 bg-white hover:shadow-xl hover:border-blue-300 transition-all cursor-pointer group hover-lift" onClick={() => navigate(role.path)} data-testid={`role-${role.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="pt-8 pb-7 px-6 text-center">
                <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-blue-600 transition-colors">
                  <role.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{role.title}</h3>
                <p className="text-sm text-slate-500 mb-5">{role.desc}</p>
                <Button className={`gap-2 w-full ${role.primary ? "bg-slate-900 text-white hover:bg-slate-800 group-hover:bg-blue-600" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 group-hover:border-blue-300 group-hover:text-blue-700"} transition-colors`}>
                  Explore {role.title.split(" ")[0]} <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Section>
  );
}
